import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
// eslint-disable-next-line @typescript-eslint/naming-convention
import CodeMirror from 'codemirror';
import 'codemirror/mode/python/python';
import 'codemirror/addon/edit/closebrackets';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';

@DecorateUntilDestroy()
@Component({
  selector: 'skypro-code-editor',
  templateUrl: 'code-editor.html',
  styleUrls: ['code-editor.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CodeEditorComponent implements OnChanges, OnInit {
  @Input() code = '';
  @Input() lang = 'python';
  @Input() closeBracket = false;

  @Output() codeChange = new EventEmitter<string>();

  @ViewChild('codeRef', { static: true }) codeRef: ElementRef<HTMLTextAreaElement>;

  codeMirror: CodeMirror.Editor;

  private codeUpdated = new Subject<string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.code && !changes.code.firstChange) {
      this.codeUpdated.next(changes.code.currentValue);
    }
  }

  ngOnInit(): void {
    if (this.codeRef) {
      this.codeMirror = CodeMirror.fromTextArea(this.codeRef.nativeElement, {
        value: this.code,
        lineNumbers: true,
        indentUnit: 2,
        tabSize: 2,
        electricChars: false,
        mode: this.lang,
        theme: 'neo',
        autoCloseBrackets: this.closeBracket,
      });

      this.codeMirror.on('change', () => {
        const newValue = this.codeMirror.getValue();
        if (newValue !== this.code) {
          this.code = newValue;
          this.onCodeChange(newValue);
        }
      });

      this.codeUpdated
        .pipe(
          distinctUntilChanged(),
          filter(code => code !== this.codeMirror.getValue()),
          takeUntilDestroyed(this),
        )
        .subscribe(code => this.codeMirror.setValue(code));
    }
  }

  onCodeChange(code: string): void {
    this.codeChange.emit(code);
  }
}
